// ApiKeys — Integrations: keys, webhooks, sync logs, API explorer (live API)
import { useEffect, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import type { ApiKey, Webhook } from "@/lib/types";

type Tab = "keys" | "webhooks" | "logs" | "explorer";

const ENDPOINTS = [
  { group: "Domains", items: [
    { m: "GET", p: "/api/domains", d: "List all domains with record counts" },
    { m: "GET", p: "/api/domains/instruments/schema", d: "Domain field schema" },
  ]},
  { group: "Records", items: [
    { m: "GET", p: "/api/domains/instruments/records", d: "List records" },
  ]},
  { group: "Approvals", items: [
    { m: "GET", p: "/api/approvals", d: "List approvals" },
  ]},
  { group: "Hierarchies", items: [
    { m: "GET", p: "/api/hierarchies", d: "List hierarchies" },
  ]},
  { group: "Mappings", items: [
    { m: "GET", p: "/api/mappings", d: "List mapping sets" },
  ]},
  { group: "Quality", items: [
    { m: "GET", p: "/api/quality/rules", d: "List quality rules" },
    { m: "GET", p: "/api/quality/violations", d: "Recent violations" },
  ]},
  { group: "Search", items: [
    { m: "GET", p: "/api/search?q=apple", d: "Cross-domain search" },
  ]},
];

const METHOD_COLOR: Record<string, string> = {
  GET: "var(--forest-500)", POST: "var(--md-primary)", PATCH: "var(--ochre-400)", DELETE: "var(--md-error)",
};

const ALL_SCOPES = ["records.read", "records.write", "approvals.approve", "domains.read", "audit.read"];
const ALL_EVENTS = ["records.created", "records.updated", "approvals.approved", "approvals.rejected"];

export function ApiKeys() {
  const { tab: tabParam } = useParams();
  const [tab, setTab] = useState<Tab>((["keys", "webhooks", "logs", "explorer"].includes(tabParam || "") ? tabParam : "keys") as Tab);

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [logs, setLogs] = useState<Array<{ id: string; at: string; direction: string; source: string; event: string; status: number; bytes: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [addingHook, setAddingHook] = useState(false);
  const toast = useToast();

  async function loadKeys() { setKeys(await api.integrations.apiKeys.list()); }
  async function loadHooks() { setWebhooks(await api.integrations.webhooks.list()); }
  async function loadLogs() { setLogs(await api.integrations.syncLogs(25)); }

  async function loadAll() {
    setLoading(true); setError(null);
    try { await Promise.all([loadKeys(), loadHooks(), loadLogs()]); }
    catch (e) { setError(e instanceof Error ? e.message : "Failed to load"); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, []);

  const revokeKey = async (k: ApiKey) => {
    if (!confirm(`Revoke ${k.name}?`)) return;
    try {
      await api.integrations.apiKeys.revoke(k.id);
      toast(`Revoked ${k.name}`, "warn");
      await loadKeys();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Revoke failed", "error");
    }
  };

  const removeHook = async (h: Webhook) => {
    if (!confirm(`Remove webhook ${h.url}?`)) return;
    try {
      await api.integrations.webhooks.remove(h.id);
      toast("Webhook removed", "ok");
      await loadHooks();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Remove failed", "error");
    }
  };

  const testHook = async (h: Webhook) => {
    try {
      const r = await api.integrations.webhooks.test(h.id);
      toast(`Test → ${r.status} in ${r.ms}ms`, r.ok ? "ok" : "warn");
      await loadHooks();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Test failed", "error");
    }
  };

  if (loading) return <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>Loading integrations…</div>;
  if (error) return <div style={{ padding: 24, color: "var(--md-error)" }}>{error}</div>;

  return (
    <div style={{ padding: 16, display: "grid", gap: 16 }}>
      <header style={{ ...panel, padding: 20 }}>
        <div style={eyebrow}>EXTERNAL</div>
        <div style={{ font: "var(--t-headline-md)", fontStyle: "italic", fontWeight: 400 }}>Integrations</div>
        <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
          {(["keys", "webhooks", "logs", "explorer"] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ ...pillBtn, ...(tab === t ? pillBtnActive : {}) }}>
              {t === "keys" ? "API Keys" : t === "webhooks" ? "Webhooks" : t === "logs" ? "Sync Logs" : "API Explorer"}
            </button>
          ))}
        </div>
      </header>

      {tab === "keys" && (
        <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: 14, display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--md-outline-var)" }}>
            <div style={{ font: "var(--t-title-md)" }}>API Keys ({keys.length})</div>
            <button onClick={() => setCreating(true)} style={filledBtn}>+ Create key</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Name</th><th style={th}>Prefix</th><th style={th}>Scopes</th><th style={th}>Created by</th><th style={th}>Last used</th><th style={th}></th></tr></thead>
            <tbody>
              {keys.filter(k => !k.revoked).map(k => (
                <tr key={k.id} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                  <td style={td}>{k.name}</td>
                  <td style={{ ...td, font: "var(--t-mono-sm)" }}>{k.prefix}••••</td>
                  <td style={td}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{k.scopes.map(s => <span key={s} style={chipSm}>{s}</span>)}</div></td>
                  <td style={td}>{k.createdBy}</td>
                  <td style={{ ...td, color: "var(--md-on-surface-var)", font: "var(--t-mono-sm)" }}>{k.lastUsedAt ?? "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button onClick={() => revokeKey(k)} style={{ ...pillBtn, color: "var(--md-error)" }}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "webhooks" && (
        <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: 14, display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--md-outline-var)" }}>
            <div style={{ font: "var(--t-title-md)" }}>Webhooks ({webhooks.length})</div>
            <button style={filledBtn} onClick={() => setAddingHook(true)}>+ Add webhook</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>URL</th><th style={th}>Events</th><th style={th}>Last status</th><th style={th}>Last delivery</th><th style={th}></th></tr></thead>
            <tbody>
              {webhooks.map(h => (
                <tr key={h.id} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                  <td style={{ ...td, font: "var(--t-mono-sm)", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.url}</td>
                  <td style={td}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{h.events.map(e => <span key={e} style={chipSm}>{e}</span>)}</div></td>
                  <td style={td}>
                    <span style={{
                      padding: "2px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                      background: (h.lastStatus ?? 0) < 400 && (h.lastStatus ?? 0) > 0 ? "var(--md-secondary-container)" : "var(--md-sc-high)",
                      color: (h.lastStatus ?? 0) < 400 && (h.lastStatus ?? 0) > 0 ? "var(--md-on-secondary-container)" : "var(--md-on-surface-var)",
                    }}>{h.lastStatus ?? "—"}</span>
                  </td>
                  <td style={{ ...td, font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{h.lastDeliveryAt ?? "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button style={pillBtn} onClick={() => testHook(h)}>Send test</button>
                    <button style={{ ...pillBtn, marginLeft: 6, color: "var(--md-error)" }} onClick={() => removeHook(h)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "logs" && (
        <div style={{ ...panel, padding: 0 }}>
          <div style={{ padding: 14, font: "var(--t-title-md)", borderBottom: "1px solid var(--md-outline-var)" }}>Sync logs (last {logs.length})</div>
          {logs.length === 0 && <div style={{ padding: 24, color: "var(--md-on-surface-var)" }}>No recent events.</div>}
          {logs.map((l, i) => (
            <div key={l.id} style={{ display: "grid", gridTemplateColumns: "160px 24px 1fr auto 90px", gap: 12, padding: "12px 16px", borderTop: i ? "1px solid var(--md-outline-var)" : "none", alignItems: "center" }}>
              <span style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{l.at.slice(0, 19).replace("T", " ")}</span>
              <span style={{ font: "var(--t-headline-sm)", color: l.direction === "in" ? "var(--forest-500)" : "var(--md-primary)" }}>{l.direction === "in" ? "↓" : "↑"}</span>
              <span><strong>{l.source}</strong> · <span style={{ color: "var(--md-on-surface-var)" }}>{l.event}</span></span>
              <span style={{
                padding: "2px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                background: l.status < 400 ? "var(--md-secondary-container)" : "var(--md-error-container)",
                color: l.status < 400 ? "var(--md-on-secondary-container)" : "var(--md-on-error-container)",
              }}>{l.status}</span>
              <span style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)", textAlign: "right" }}>{l.id}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "explorer" && <ApiExplorer />}

      {creating && (
        <div style={scrim} onClick={() => { setCreating(false); setRevealed(null); }} role="dialog" aria-modal="true">
          <div style={dialog} onClick={e => e.stopPropagation()}>
            <div style={eyebrow}>NEW KEY</div>
            <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400, marginBottom: 14 }}>Create API key</div>
            {!revealed ? (
              <CreateKeyForm onCreate={async (name, scopes) => {
                try {
                  const r = await api.integrations.apiKeys.create(name, scopes);
                  setRevealed(r.key);
                  toast(`Key "${name}" created`, "ok");
                  await loadKeys();
                } catch (e) {
                  toast(e instanceof Error ? e.message : "Create failed", "error");
                }
              }} onCancel={() => setCreating(false)} />
            ) : (
              <div>
                <div style={{ padding: 14, background: "var(--md-tertiary-container)", color: "var(--md-on-tertiary-container)", borderRadius: "var(--r-md)", font: "var(--t-body-sm)" }}>
                  Copy this secret now — it won't be shown again.
                </div>
                <div style={{ marginTop: 12, font: "var(--t-mono-sm)", padding: 12, background: "var(--md-sc-low)", borderRadius: "var(--r-md)", wordBreak: "break-all" }}>{revealed}</div>
                <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button onClick={() => { navigator.clipboard?.writeText(revealed); toast("Copied", "ok"); }} style={pillBtn}>Copy</button>
                  <button onClick={() => { setCreating(false); setRevealed(null); }} style={filledBtn}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {addingHook && (
        <div style={scrim} onClick={() => setAddingHook(false)} role="dialog" aria-modal="true">
          <div style={dialog} onClick={e => e.stopPropagation()}>
            <div style={eyebrow}>NEW WEBHOOK</div>
            <div style={{ font: "var(--t-headline-sm)", fontStyle: "italic", fontWeight: 400, marginBottom: 14 }}>Add webhook</div>
            <CreateHookForm onCreate={async (url, events) => {
              try {
                await api.integrations.webhooks.create(url, events);
                toast("Webhook added", "ok");
                setAddingHook(false);
                await loadHooks();
              } catch (e) {
                toast(e instanceof Error ? e.message : "Create failed", "error");
              }
            }} onCancel={() => setAddingHook(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function CreateKeyForm({ onCreate, onCancel }: { onCreate: (n: string, s: string[]) => void | Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["records.read"]);
  return (
    <>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>Name</span>
        <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="e.g. Snowflake sync" />
      </label>
      <div style={{ marginTop: 12 }}>
        <div style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", marginBottom: 6 }}>Scopes</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ALL_SCOPES.map(s => (
            <button key={s} onClick={() => setScopes(sc => sc.includes(s) ? sc.filter(x => x !== s) : [...sc, s])}
              style={{ ...pillBtn, ...(scopes.includes(s) ? pillBtnActive : {}) }}>{s}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button onClick={onCancel} style={pillBtn}>Cancel</button>
        <button onClick={() => onCreate(name, scopes)} disabled={!name} style={filledBtn}>Create</button>
      </div>
    </>
  );
}

function CreateHookForm({ onCreate, onCancel }: { onCreate: (url: string, events: string[]) => void | Promise<void>; onCancel: () => void }) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["records.created"]);
  return (
    <>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)" }}>URL</span>
        <input value={url} onChange={e => setUrl(e.target.value)} style={input} placeholder="https://example.com/hook" />
      </label>
      <div style={{ marginTop: 12 }}>
        <div style={{ font: "var(--t-label-sm)", color: "var(--md-on-surface-var)", marginBottom: 6 }}>Events</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {ALL_EVENTS.map(s => (
            <button key={s} onClick={() => setEvents(ev => ev.includes(s) ? ev.filter(x => x !== s) : [...ev, s])}
              style={{ ...pillBtn, ...(events.includes(s) ? pillBtnActive : {}) }}>{s}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <button onClick={onCancel} style={pillBtn}>Cancel</button>
        <button onClick={() => onCreate(url, events)} disabled={!url || events.length === 0} style={filledBtn}>Create</button>
      </div>
    </>
  );
}

function ApiExplorer() {
  const [open, setOpen] = useState<string>("GET /api/domains");
  const [path, setPath] = useState<string>("/api/domains");
  const [response, setResponse] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const ep = ENDPOINTS.flatMap(g => g.items).find(i => `${i.m} ${i.p}` === open);
    if (ep) setPath(ep.p);
  }, [open]);

  const send = async () => {
    setRunning(true);
    setResponse(null);
    setStatus(null);
    const [m] = open.split(" ");
    try {
      const r = await api.raw(m, path);
      setStatus(r.status);
      setResponse(typeof r.body === "string" ? r.body : JSON.stringify(r.body, null, 2));
    } catch (e) {
      setResponse(e instanceof Error ? e.message : "request failed");
      setStatus(0);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
      <aside style={{ ...panel, padding: 12, height: "70vh", overflow: "auto" }} className="scroll">
        {ENDPOINTS.map(g => (
          <div key={g.group} style={{ marginBottom: 14 }}>
            <div style={{ ...eyebrow, marginBottom: 6 }}>{g.group.toUpperCase()}</div>
            {g.items.map(it => {
              const key = `${it.m} ${it.p}`;
              return (
                <button key={key} onClick={() => setOpen(key)} style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                  padding: "8px 10px", borderRadius: "var(--r-md)", border: "none",
                  background: open === key ? "var(--md-secondary-container)" : "transparent",
                  color: open === key ? "var(--md-on-secondary-container)" : "var(--md-on-surface)",
                  cursor: "pointer", font: "var(--t-body-sm)",
                }}>
                  <span style={{ font: "var(--t-label-sm)", color: METHOD_COLOR[it.m], minWidth: 48 }}>{it.m}</span>
                  <span style={{ font: "var(--t-mono-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.p}</span>
                </button>
              );
            })}
          </div>
        ))}
      </aside>

      <section style={{ ...panel, padding: 20, height: "70vh", overflow: "auto" }} className="scroll">
        {(() => {
          const [m] = open.split(" ");
          const ep = ENDPOINTS.flatMap(g => g.items).find(i => `${i.m} ${i.p}` === open);
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ font: "var(--t-title-md)", color: METHOD_COLOR[m] }}>{m}</span>
                <input value={path} onChange={e => setPath(e.target.value)} style={{ ...input, font: "var(--t-mono-md)" }} />
                <button onClick={send} disabled={running} style={filledBtn}>{running ? "Sending…" : "Send"}</button>
              </div>
              <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)", marginTop: 8 }}>{ep?.d}</div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20 }}>
                <div style={eyebrow}>RESPONSE</div>
                {status != null && (
                  <span style={{
                    padding: "2px 10px", borderRadius: "var(--r-pill)", font: "var(--t-label-sm)",
                    background: status < 400 && status > 0 ? "var(--md-secondary-container)" : "var(--md-error-container)",
                    color: status < 400 && status > 0 ? "var(--md-on-secondary-container)" : "var(--md-on-error-container)",
                  }}>{status || "ERR"}</span>
                )}
              </div>
              <pre style={{
                margin: "8px 0 0", padding: 14, background: "var(--md-sc-low)", borderRadius: "var(--r-md)",
                font: "var(--t-mono-sm)", maxHeight: 480, overflow: "auto",
              }} className="scroll">
                {response ?? "Send a request to see the response."}
              </pre>
            </>
          );
        })()}
      </section>
    </div>
  );
}

const panel: CSSProperties = { background: "var(--md-sc-lowest)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)" };
const eyebrow: CSSProperties = { font: "var(--t-label-sm)", color: "var(--md-tertiary)", letterSpacing: "0.12em" };
const input: CSSProperties = { padding: "8px 12px", borderRadius: "var(--r-md)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-low)", color: "var(--md-on-surface)", font: "var(--t-body-md)", width: "100%" };
const pillBtn: CSSProperties = { padding: "6px 12px", borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)", background: "var(--md-sc-lowest)", color: "var(--md-on-surface)", cursor: "pointer", font: "var(--t-label-md)" };
const pillBtnActive: CSSProperties = { background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)", borderColor: "transparent" };
const filledBtn: CSSProperties = { padding: "8px 18px", borderRadius: "var(--r-pill)", border: "none", background: "var(--md-primary)", color: "var(--md-on-primary)", cursor: "pointer", font: "var(--t-label-lg)" };
const th: CSSProperties = { textAlign: "left", padding: "10px 14px", font: "var(--t-label-md)", color: "var(--md-on-surface-var)" };
const td: CSSProperties = { padding: "10px 14px", font: "var(--t-body-md)" };
const chipSm: CSSProperties = { padding: "2px 8px", borderRadius: "var(--r-pill)", background: "var(--md-sc-high)", font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" };
const scrim: CSSProperties = { position: "fixed", inset: 0, background: "var(--md-scrim)", display: "grid", placeItems: "center", zIndex: 100 };
const dialog: CSSProperties = { width: 520, maxWidth: "90vw", background: "var(--md-sc-lowest)", padding: 24, borderRadius: "var(--r-xl)", boxShadow: "var(--e-4)" };

export default ApiKeys;
